// PvP Arena tab — entry point with 3 sub-views: Lobby, DeckBuilder, Leaderboard
// Match history shown inline on the Lobby. Real-time battle WS hookup arrives
// in the next iteration; for now the "Inizia battaglia" button placeholders
// show a toast.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Swords, Trophy, Layers, Loader2, Copy, Check, X, ChevronRight, Crown, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { resolveImageUrl } from "@/utils/imageUrl";
import { fallbackCopy } from "@/utils/clipboard";
import useTabMusic from "@/hooks/useTabMusic";
import ArenaBattle from "./ArenaBattle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const QUEUE_POLL_MS = 3000;
const INVITE_POLL_MS = 4000;

const RARITY_COLOR = {
  common: "#cbd5e1",
  uncommon: "#34d399",
  rare: "#60a5fa",
  epic: "#a78bfa",
  legendary: "#fbbf24",
};

function formatDuration(secs, isIt) {
  if (!secs || secs <= 0) return isIt ? "termina ora" : "ending now";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}${isIt ? "g" : "d"} ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ============ DECK BUILDER ============
function DeckBuilder({ onDeckSaved, onCancel }) {
  const { game } = useGame();
  const { token, API } = useAuth();
  const [eligible, setEligible] = useState([]);
  const [deckSize, setDeckSize] = useState(15);
  const [selected, setSelected] = useState([]); // array of card ids
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isIt = (game?.language || "en").startsWith("it");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [cardsRes, deckRes] = await Promise.all([
          axios.get(`${API}/game/${game.id}/arena/cards`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/game/${game.id}/arena/deck`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!mounted) return;
        setEligible(cardsRes.data.cards || []);
        setDeckSize(cardsRes.data.deck_size || 15);
        setSelected((deckRes.data.deck && deckRes.data.deck.card_ids) || []);
      } catch {
        toast.error(isIt ? "Errore caricamento carte" : "Failed to load cards");
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [game?.id, token, API, isIt]);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= deckSize) {
        toast.warning(isIt ? `Massimo ${deckSize} carte` : `Maximum ${deckSize} cards`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const save = async () => {
    if (selected.length !== deckSize) {
      toast.error(isIt ? `Servono esattamente ${deckSize} carte` : `Need exactly ${deckSize} cards`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/game/${game.id}/arena/deck`,
        { card_ids: selected },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(isIt ? "Mazzo salvato" : "Deck saved");
      onDeckSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Salvataggio fallito" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={28} className="animate-spin text-[#cfb53b]" />
      </div>
    );
  }

  if (eligible.length < deckSize) {
    return (
      <div className="text-center py-12 px-4" data-testid="deck-builder-not-enough">
        <Layers size={36} className="mx-auto mb-3 text-[#94a3b8]/40" />
        <p className="text-[#cbd5e1] mb-2 font-heading">
          {isIt ? `Servono almeno ${deckSize} carte uniche` : `You need at least ${deckSize} unique cards`}
        </p>
        <p className="text-xs text-[#64748b]">
          {isIt
            ? `Hai ${eligible.length} carte. Continua la storia, vinci battaglie e apri pacchetti per ottenerne altre.`
            : `You have ${eligible.length} cards. Keep playing, fight battles and open packs to gain more.`}
        </p>
        <Button variant="outline" onClick={onCancel} className="mt-4 border-[#cfb53b]/30 text-[#cfb53b]">
          {isIt ? "Indietro" : "Back"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="deck-builder">
      <div className="flex items-center justify-between gap-2 sticky top-0 z-10 bg-[#0a0a0c]/95 backdrop-blur py-2 -mx-2 px-2 border-b border-[#2d3748]">
        <div className="font-heading">
          <span className={`text-base ${selected.length === deckSize ? "text-[#34d399]" : "text-[#cfb53b]"}`}>
            {selected.length}/{deckSize}
          </span>
          <span className="text-[10px] text-[#64748b] ml-2">{isIt ? "carte selezionate" : "selected"}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} className="text-[#94a3b8]">
            {isIt ? "Annulla" : "Cancel"}
          </Button>
          <Button
            onClick={save}
            disabled={saving || selected.length !== deckSize}
            data-testid="save-deck-btn"
            className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
            {isIt ? "Salva mazzo" : "Save deck"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="deck-builder-grid">
        {eligible.map((c) => {
          const sel = selected.includes(c.id);
          const color = RARITY_COLOR[(c.rarity || "common").toLowerCase()] || RARITY_COLOR.common;
          return (
            <button
              key={c.id}
              onClick={() => toggle(c.id)}
              data-testid={`deck-card-${c.id}`}
              className={`relative text-left rounded-lg border-2 transition-all overflow-hidden ${
                sel
                  ? "border-[#34d399] bg-[#34d399]/10 ring-2 ring-[#34d399]/30"
                  : "border-[#2d3748] bg-[#1a1a1d] hover:border-[#cfb53b]/50"
              }`}
            >
              {sel && (
                <div className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-[#34d399] flex items-center justify-center shadow">
                  <Check size={11} className="text-[#0a0a0c]" />
                </div>
              )}
              <div className="aspect-[3/4] bg-[#0a0a0c] flex items-center justify-center">
                {c.image_url ? (
                  <img src={resolveImageUrl(c.image_url, BACKEND_URL)} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <Layers size={28} className="text-[#64748b]" />
                )}
              </div>
              <div className="p-1.5">
                <p className="font-heading text-[11px] truncate" style={{ color }}>{c.name}</p>
                <p className="text-[9px] text-[#64748b] capitalize truncate">{c.category} · {c.rarity || "common"}</p>
                <div className="flex gap-1 mt-0.5 text-[9px]">
                  {c.attack > 0 && <span className="text-red-300">⚔{c.attack}</span>}
                  {c.defense > 0 && <span className="text-blue-300">🛡{c.defense}</span>}
                  {c.hp > 0 && <span className="text-green-300">♥{c.hp}</span>}
                  {c.mana_cost > 0 && <span className="text-cyan-300">◇{c.mana_cost}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============ LEADERBOARD ============
function Leaderboard() {
  const { game } = useGame();
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const isIt = (game?.language || "en").startsWith("it");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/arena/leaderboard?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch { /* silent */ }
    })();
  }, [token, API]);

  if (!data) {
    return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#cfb53b]" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="arena-leaderboard">
      <div className="bg-gradient-to-r from-[#cfb53b]/15 via-[#cfb53b]/8 to-transparent border border-[#cfb53b]/30 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-heading text-xs text-[#cfb53b]">{isIt ? "Il tuo rank" : "Your rank"}</p>
            <p className="font-heading text-2xl text-[#e2e8f0]">
              {data.me.rank ? `#${data.me.rank}` : "—"}
              <span className="text-sm text-[#fbbf24] ml-2">{data.me.mmr} MMR</span>
            </p>
          </div>
          <div className="text-right text-xs">
            <p className="text-[#34d399]">{data.me.wins} W</p>
            <p className="text-[#f87171]">{data.me.losses} L</p>
            {data.me.win_streak > 0 && <p className="text-[#fbbf24] mt-1">🔥 {data.me.win_streak}</p>}
          </div>
        </div>
      </div>

      {data.leaderboard.length === 0 ? (
        <p className="text-center text-[#64748b] text-sm py-8">
          {isIt ? "Nessuno ha ancora combattuto. Sii il primo!" : "No-one has fought yet. Be the first!"}
        </p>
      ) : (
        <div className="space-y-1">
          {data.leaderboard.map((row) => (
            <div
              key={row.user_id}
              className="flex items-center gap-2 bg-[#1a1a1d] border border-[#2d3748] rounded-md px-3 py-2 text-xs"
              data-testid={`lb-row-${row.rank}`}
            >
              <span className={`font-heading w-7 text-right ${
                row.rank === 1 ? "text-[#fbbf24]" : row.rank === 2 ? "text-[#cbd5e1]" : row.rank === 3 ? "text-[#a78bfa]" : "text-[#64748b]"
              }`}>
                {row.rank <= 3 ? <Crown size={12} className="inline" /> : `#${row.rank}`}
              </span>
              <span className="flex-1 truncate text-[#e2e8f0]">{row.username}</span>
              <span className="text-[#fbbf24]">{row.mmr}</span>
              <span className="text-[10px] text-[#64748b]">{row.wins}W/{row.losses}L</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ MATCH HISTORY ============
function MatchHistory() {
  const { game } = useGame();
  const { token, API } = useAuth();
  const [matches, setMatches] = useState(null);
  const isIt = (game?.language || "en").startsWith("it");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/arena/history?limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMatches(r.data.matches || []);
      } catch { setMatches([]); }
    })();
  }, [token, API]);

  if (matches === null) {
    return <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-[#cfb53b]" /></div>;
  }
  if (matches.length === 0) {
    return <p className="text-center text-[#64748b] text-xs py-4">{isIt ? "Nessun match recente" : "No recent matches"}</p>;
  }
  return (
    <div className="space-y-1.5" data-testid="match-history">
      {matches.map((m) => {
        const win = m.result === "win";
        return (
          <div key={m.id} className="flex items-center gap-2 bg-[#1a1a1d] border border-[#2d3748] rounded px-2.5 py-1.5 text-[11px]" data-testid={`match-${m.id}`}>
            <span className={`font-heading w-10 ${win ? "text-[#34d399]" : "text-[#f87171]"}`}>
              {win ? "WIN" : "LOSS"}
            </span>
            <span className="flex-1 truncate text-[#cbd5e1]">vs {m.opponent?.username || "—"}</span>
            <span className="text-[10px] text-[#64748b]">{(m.ended_at || "").slice(0, 10)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ============ MAIN ARENA TAB ============
export default function Arena() {
  const { game } = useGame();
  const { token, API } = useAuth();
  const [view, setView] = useState("lobby"); // lobby | deck | leaderboard
  const [hasDeck, setHasDeck] = useState(null); // null=loading, bool=resolved
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [activeInvite, setActiveInvite] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [queueWaitedSec, setQueueWaitedSec] = useState(0);
  const [copied, setCopied] = useState(false);
  const [activeMatch, setActiveMatch] = useState(null); // {id, opponent_username}
  const [matchFormat, setMatchFormat] = useState("bo1"); // bo1 | bo3
  const [season, setSeason] = useState(null); // {active, time_left_seconds, past}

  // iter 74 — switch to the appropriate music for the active sub-view
  // (lobby / deck builder / leaderboard → tense arena prelude;
  // active match → competitive arena_battle)
  useTabMusic(activeMatch ? 'arena_battle' : 'arena');

  const isIt = (game?.language || "en").startsWith("it");

  const enterMatch = useCallback((match) => {
    if (!match?.id) return;
    setActiveMatch({ id: match.id, opponent_username: match.opponent_username || null });
    setActiveInvite(null);
    setQueueing(false);
  }, []);

  const checkDeck = useCallback(async () => {
    if (!game?.id) return;
    try {
      const r = await axios.get(`${API}/game/${game.id}/arena/deck`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHasDeck(!!r.data.deck);
    } catch {
      setHasDeck(false);
    }
  }, [game?.id, token, API]);

  useEffect(() => { checkDeck(); }, [checkDeck]);

  // Fetch season info on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/arena/season`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSeason(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [token, API]);

  // Poll invite status (host side) — when guest joins, navigate to match
  useEffect(() => {
    if (!activeInvite?.code) return;
    let active = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/arena/invite/${activeInvite.code}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        if (r.data.match) {
          setActiveInvite(null);
          toast.success(isIt ? "Avversario trovato!" : "Opponent found!");
          enterMatch(r.data.match);
        }
      } catch { /* keep polling */ }
    };
    const id = setInterval(tick, INVITE_POLL_MS);
    return () => { active = false; clearInterval(id); };
  }, [activeInvite?.code, token, API, isIt]);

  // Poll matchmaking queue (guest pattern: my client looks for matches)
  useEffect(() => {
    if (!queueing) return;
    let active = true;
    const start = Date.now();
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/arena/queue/poll`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        setQueueWaitedSec(Math.floor((Date.now() - start) / 1000));
        if (r.data.match) {
          setQueueing(false);
          toast.success(isIt ? "Avversario trovato!" : "Opponent found!");
          enterMatch(r.data.match);
        }
      } catch { /* keep polling */ }
    };
    const id = setInterval(tick, QUEUE_POLL_MS);
    return () => { active = false; clearInterval(id); };
  }, [queueing, token, API, isIt]);

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const r = await axios.post(`${API}/arena/invite`, { game_id: game.id, match_format: matchFormat }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveInvite(r.data);
      toast.success(isIt ? "Codice generato. Condividilo con un amico." : "Code generated. Share with a friend.");
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Creazione invito fallita" : "Invite creation failed"));
    } finally {
      setCreatingInvite(false);
    }
  };

  const cancelInvite = async () => {
    if (!activeInvite?.code) return;
    try {
      await axios.delete(`${API}/arena/invite/${activeInvite.code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* ignore */ }
    setActiveInvite(null);
  };

  const joinByCode = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const r = await axios.post(
        `${API}/arena/invite/join`,
        { code: joinCode.trim().toUpperCase(), game_id: game.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(isIt ? "Ti sei unito al match!" : "Joined the match!");
      setJoinCode("");
      setActiveInvite(null);
      enterMatch(r.data.match);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Codice non valido" : "Invalid code"));
    } finally {
      setJoining(false);
    }
  };

  const joinQueue = async () => {
    setQueueing(true);
    setQueueWaitedSec(0);
    try {
      await axios.post(`${API}/arena/queue/join`, { game_id: game.id, format: matchFormat }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(isIt ? "In coda — cerco un avversario..." : "Queued — searching for opponent...");
    } catch (e) {
      setQueueing(false);
      toast.error(e.response?.data?.detail || (isIt ? "Coda fallita" : "Queue failed"));
    }
  };

  const leaveQueue = async () => {
    try {
      await axios.delete(`${API}/arena/queue`, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* ignore */ }
    setQueueing(false);
  };

  const copyInvite = () => {
    if (!activeInvite?.code) return;
    fallbackCopy(activeInvite.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success(isIt ? "Copiato" : "Copied");
  };

  if (hasDeck === null) {
    return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#cfb53b]" /></div>;
  }

  // ----- ACTIVE MATCH (real-time WS battle) -----
  if (activeMatch) {
    return (
      <ArenaBattle
        matchId={activeMatch.id}
        opponentName={activeMatch.opponent_username}
        onExit={() => setActiveMatch(null)}
      />
    );
  }

  // ----- DECK BUILDER VIEW -----
  if (view === "deck") {
    return (
      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Layers size={18} className="text-[#cfb53b]" />
          <h2 className="font-heading text-lg text-[#cfb53b]">{isIt ? "Costruisci il mazzo" : "Build deck"}</h2>
        </div>
        <DeckBuilder
          onDeckSaved={() => { setHasDeck(true); setView("lobby"); }}
          onCancel={() => setView("lobby")}
        />
      </div>
    );
  }

  // ----- LEADERBOARD VIEW -----
  if (view === "leaderboard") {
    return (
      <div className="max-w-2xl mx-auto px-2 sm:px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-[#cfb53b]" />
            <h2 className="font-heading text-lg text-[#cfb53b]">{isIt ? "Classifica" : "Leaderboard"}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setView("lobby")} className="text-[#94a3b8]">
            {isIt ? "Indietro" : "Back"}
          </Button>
        </div>
        <Leaderboard />
      </div>
    );
  }

  // ----- LOBBY VIEW (default) -----
  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-4 py-4 space-y-4" data-testid="arena-lobby">
      <div className="flex items-center gap-2">
        <Swords size={20} className="text-[#cfb53b]" />
        <h1 className="font-heading text-xl sm:text-2xl text-[#cfb53b]">{isIt ? "Arena PvP" : "Arena PvP"}</h1>
      </div>

      {/* Season banner */}
      {season?.active && (
        <div className="bg-gradient-to-r from-[#a78bfa]/15 via-[#cfb53b]/10 to-transparent border border-[#a78bfa]/30 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid="arena-season-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Crown size={14} className="text-[#a78bfa] shrink-0" />
            <div className="min-w-0">
              <p className="font-heading text-sm text-[#a78bfa] truncate">{season.active.name}</p>
              <p className="text-[10px] text-[#94a3b8]">
                {isIt ? "Termina tra" : "Ends in"} {formatDuration(season.time_left_seconds, isIt)}
              </p>
            </div>
          </div>
          {season.past && season.past.length > 0 && season.past[0].top_3 && season.past[0].top_3.length > 0 && (
            <div className="text-right text-[10px] text-[#94a3b8] hidden sm:block">
              <p className="font-heading text-[#cfb53b]">{isIt ? `🏆 ${season.past[0].name}` : `🏆 ${season.past[0].name}`}</p>
              <p className="truncate">#1: {season.past[0].top_3[0]?.username}</p>
            </div>
          )}
        </div>
      )}

      {/* Deck status */}
      <div className={`rounded-lg p-3 border ${hasDeck ? "border-[#34d399]/30 bg-[#34d399]/5" : "border-[#fbbf24]/30 bg-[#fbbf24]/5"}`} data-testid="arena-deck-status">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Layers size={14} className={hasDeck ? "text-[#34d399]" : "text-[#fbbf24]"} />
            <p className="text-xs text-[#cbd5e1]">
              {hasDeck
                ? (isIt ? "Mazzo pronto" : "Deck ready")
                : (isIt ? "Mazzo non configurato" : "Deck not configured")}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setView("deck")}
            data-testid="arena-build-deck-btn"
            className={hasDeck ? "border-[#34d399]/40 text-[#34d399]" : "border-[#fbbf24]/40 text-[#fbbf24]"}
          >
            {hasDeck ? (isIt ? "Modifica" : "Edit") : (isIt ? "Configura" : "Build")}
          </Button>
        </div>
      </div>

      {/* Match actions */}
      {hasDeck && !activeInvite && !queueing && (
        <>
          {/* Format selector BO1/BO3 */}
          <div className="flex items-center justify-center gap-2 text-xs" data-testid="arena-format-selector">
            <span className="text-[#94a3b8]">{isIt ? "Formato:" : "Format:"}</span>
            <button
              onClick={() => setMatchFormat("bo1")}
              data-testid="format-bo1"
              className={`px-3 py-1 rounded-full font-heading transition-colors ${
                matchFormat === "bo1"
                  ? "bg-[#cfb53b] text-[#0a0a0c]"
                  : "bg-[#1a1a1d] text-[#94a3b8] border border-[#2d3748] hover:text-[#cfb53b]"
              }`}
            >
              {isIt ? "Best of 1" : "Best of 1"}
            </button>
            <button
              onClick={() => setMatchFormat("bo3")}
              data-testid="format-bo3"
              className={`px-3 py-1 rounded-full font-heading transition-colors ${
                matchFormat === "bo3"
                  ? "bg-[#a78bfa] text-[#0a0a0c]"
                  : "bg-[#1a1a1d] text-[#94a3b8] border border-[#2d3748] hover:text-[#a78bfa]"
              }`}
            >
              {isIt ? "Best of 3" : "Best of 3"}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              onClick={createInvite}
              disabled={creatingInvite}
              data-testid="arena-create-invite"
              className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading h-12"
            >
              {creatingInvite ? <Loader2 size={14} className="animate-spin mr-1" /> : <Swords size={14} className="mr-2" />}
              {isIt ? "Sfida un amico" : "Challenge a friend"}
            </Button>
            <Button
              onClick={joinQueue}
              data-testid="arena-join-queue"
              className="bg-[#a78bfa] hover:bg-[#9061eb] text-[#0a0a0c] font-heading h-12"
            >
              <Trophy size={14} className="mr-2" />
              {isIt ? "Trova partita" : "Find match"}
            </Button>
          </div>
        </>
      )}

      {/* Active invite */}
      {activeInvite && (
        <div className="bg-[#1a1a1d] border-2 border-[#cfb53b]/50 rounded-lg p-4 space-y-3" data-testid="arena-active-invite">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider font-heading">
            {isIt ? "Codice di sfida" : "Challenge code"}
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-center text-3xl font-heading text-[#cfb53b] tracking-[0.4em] bg-[#0a0a0c] py-3 rounded">
              {activeInvite.code}
            </code>
            <Button
              size="icon"
              variant="outline"
              onClick={copyInvite}
              data-testid="arena-copy-invite"
              className="border-[#cfb53b]/40 text-[#cfb53b] hover:bg-[#cfb53b]/10 shrink-0 h-12 w-12"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </Button>
          </div>
          <p className="text-[11px] text-[#94a3b8] text-center">
            {isIt
              ? `Condividi questo codice (valido ${activeInvite.expires_in_minutes || 10} min). In attesa dell'avversario...`
              : `Share this code (valid ${activeInvite.expires_in_minutes || 10} min). Waiting for opponent...`}
          </p>
          <div className="flex items-center justify-center gap-2 text-[#cfb53b]">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-xs">{isIt ? "In attesa..." : "Waiting..."}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={cancelInvite} className="w-full text-[#f87171]" data-testid="arena-cancel-invite">
            <X size={12} className="mr-1" /> {isIt ? "Annulla" : "Cancel"}
          </Button>
        </div>
      )}

      {/* Queueing */}
      {queueing && (
        <div className="bg-[#1a1a1d] border-2 border-[#a78bfa]/50 rounded-lg p-4 space-y-2" data-testid="arena-queueing">
          <div className="flex items-center justify-center gap-2 text-[#a78bfa]">
            <Loader2 size={20} className="animate-spin" />
            <span className="font-heading">{isIt ? "Ricerca avversario..." : "Searching opponent..."}</span>
          </div>
          <p className="text-[11px] text-[#94a3b8] text-center">{queueWaitedSec}s</p>
          <Button variant="ghost" size="sm" onClick={leaveQueue} className="w-full text-[#f87171]" data-testid="arena-leave-queue">
            <X size={12} className="mr-1" /> {isIt ? "Esci dalla coda" : "Leave queue"}
          </Button>
        </div>
      )}

      {/* Join via code */}
      {hasDeck && !activeInvite && !queueing && (
        <div className="bg-[#1a1a1d] border border-[#2d3748] rounded-lg p-3" data-testid="arena-join-form">
          <p className="text-xs text-[#94a3b8] mb-2">{isIt ? "Hai un codice di sfida?" : "Have a challenge code?"}</p>
          <div className="flex gap-2">
            <Input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABCXYZ"
              maxLength={6}
              className="bg-[#0a0a0c] border-[#2d3748] uppercase tracking-widest font-heading text-center"
              data-testid="arena-join-input"
            />
            <Button
              onClick={joinByCode}
              disabled={joining || joinCode.trim().length < 4}
              data-testid="arena-join-submit"
              className="bg-[#34d399] hover:bg-[#10b981] text-[#0a0a0c] font-heading"
            >
              {joining ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
            </Button>
          </div>
        </div>
      )}

      {/* Quick stats + leaderboard CTA */}
      <div
        className="bg-[#1a1a1d] border border-[#cfb53b]/20 rounded-lg p-3 cursor-pointer hover:border-[#cfb53b]/50 transition-colors"
        onClick={() => setView("leaderboard")}
        data-testid="arena-leaderboard-cta"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy size={14} className="text-[#cfb53b]" />
            <span className="font-heading text-sm text-[#cfb53b]">{isIt ? "Classifica generale" : "Leaderboard"}</span>
          </div>
          <ChevronRight size={14} className="text-[#94a3b8]" />
        </div>
      </div>

      {/* Match history */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <History size={14} className="text-[#94a3b8]" />
          <span className="font-heading text-sm text-[#94a3b8]">{isIt ? "Match recenti" : "Recent matches"}</span>
        </div>
        <MatchHistory />
      </div>
    </div>
  );
}
